import {z} from 'zod';

import {BaseVoidRequestSchema} from '@dms/api/shared';

/**
 * Base types
 */
const BaseSupplierSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type BaseSupplier = z.infer<typeof BaseSupplierSchema>;

const WarehouseSupplierSchema = z.object({
  id: z.string().nullable(),
});

export type WarehouseSupplier = z.infer<typeof WarehouseSupplierSchema>;

const BaseSupplierPriceListSchema = z.object({
  supplierId: z.string(),
  priceListId: z.string(),
  make: z.string().nullish(),
});

const SupplierPriceListSchema = z
  .object({priceListName: z.string()})
  .merge(BaseSupplierPriceListSchema);

export type SupplierPriceList = z.infer<typeof SupplierPriceListSchema>;

export const DiscountTypeSchema = z.enum(['PURCHASE_PRICE', 'RECOMMENDED_PRICE']);
export type DiscountType = z.infer<typeof DiscountTypeSchema>;

export const SupplierDiscountLevelSchema = z.object({
  supplierDiscountGroupId: z.string(),
  supplierOrderTypeCode: z.string().nullish(),
  supplierOrderTypeName: z.string().nullish(),
  discountPercentage: z.number().min(0).max(100).nullable().default(null),
  supplierOrderTypeIsActive: z.boolean(),
});
export type SupplierDiscountLevel = z.infer<typeof SupplierDiscountLevelSchema>;

/**
 * GET
 */
export const GetSupplierRequestSchema = z.object({
  supplierId: z.string(),
});
export type GetSupplierRequest = z.infer<typeof GetSupplierRequestSchema>;

export const GetSupplierResponseSchema = BaseSupplierSchema;
export type GetSupplierResponse = z.infer<typeof GetSupplierResponseSchema>;

export const GetSupplierDiscountGroupsRequestSchema = z.object({
  supplierId: z.string(),
  authorizationProfileId: z.string(),
  discountGroupId: z.string().nullish(),
});
export type GetSupplierDiscountGroupsRequest = z.infer<
  typeof GetSupplierDiscountGroupsRequestSchema
>;

export const GetSupplierDiscountGroupsResponseSchema = z.object({
  supplierId: z.string().nullish(),
  discountGroupCode: z.array(
    z.object({discountGroupId: z.string(), code: z.string(), isActive: z.boolean()})
  ),
});
export type GetSupplierDiscountGroupsResponse = z.infer<
  typeof GetSupplierDiscountGroupsResponseSchema
>;

export const GetSupplierDiscountGroupRequestSchema = z.object({
  supplierDiscountGroupId: z.string(),
});
export type GetSupplierDiscountGroupRequest = z.infer<typeof GetSupplierDiscountGroupRequestSchema>;

export const GetSupplierDiscountGroupResponseSchema = z.object({
  authorizationProfileId: z.string(),
  supplierId: z.string(),
  supplierDiscountGroupCode: z.string(),
  supplierDiscountGroupIsActive: z.boolean(),
  typeOfCalculation: DiscountTypeSchema,
  isActive: z.boolean(),
  discountLevels: z.array(SupplierDiscountLevelSchema),
});
export type GetSupplierDiscountGroupResponse = z.infer<
  typeof GetSupplierDiscountGroupResponseSchema
>;

/**
 * POST
 */

/**
 * PATCH
 */
export const PatchSupplierDiscountGroupRequestSchema = z.object({
  typeOfCalculation: DiscountTypeSchema,
  discountLevels: z.array(
    z.object({
      supplierDiscountGroupId: z.string(),
      discountPercentage: z.number(),
    })
  ),
});
export type PatchSupplierDiscountGroupRequest = z.infer<
  typeof PatchSupplierDiscountGroupRequestSchema
>;

export const PatchSupplierDiscountGroupResponseSchema = BaseVoidRequestSchema;
export type PatchSupplierDiscountGroupResponse = z.infer<
  typeof PatchSupplierDiscountGroupResponseSchema
>;

export const BulkPatchSupplierDiscountGroupsRequestSchema = z.object({
  typeOfCalculation: DiscountTypeSchema.nullish(),
  discountPercentage: z.number().nullish(),
  supplierDiscountGroupIds: z.array(z.string()),
});
export type BulkPatchSupplierDiscountGroupsRequest = z.infer<
  typeof BulkPatchSupplierDiscountGroupsRequestSchema
>;

export const BulkPatchSupplierDiscountGroupsResponseSchema = BaseVoidRequestSchema;
export type BulkPatchSupplierDiscountGroupsResponse = z.infer<
  typeof BulkPatchSupplierDiscountGroupsResponseSchema
>;

/**
 * PUT
 */
export const PutRefreshSupplierDiscountGroupsRequestSchema = BaseVoidRequestSchema;
export type PutRefreshSupplierDiscountGroupsRequest = z.infer<
  typeof PutRefreshSupplierDiscountGroupsRequestSchema
>;

export const PutRefreshSupplierDiscountGroupsResponseSchema = BaseVoidRequestSchema;
export type PutRefreshSupplierDiscountGroupsResponse = z.infer<
  typeof PutRefreshSupplierDiscountGroupsResponseSchema
>;
